import { Box, IconButton } from '@mui/material';
import Image from 'next/image';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { tierConfig } from 'constants/loyalty.constants';
import { LoyaltyTierName } from 'types/api/account.types';

import Icon from 'components/@icons/Icon';
import {
  GradientImage,
  NoiseImage,
} from 'components/@loyalty/Card/Card.styles';
import PortraitCardContainer from 'components/@loyalty/PortraitCardContainer';
import { getMembershipCardIcon } from 'components/@loyalty/utils';

interface Props {
  tier: LoyaltyTierName;
  children?: ReactNode;
  logoExtraSpace?: boolean;
  isProgressAnimation?: boolean;
  onClose?: () => void;
}

const LoyaltyCardPortrait = ({
  tier,
  children,
  logoExtraSpace,
  isProgressAnimation,
  onClose,
}: Props) => {
  const intl = useIntl();
  const config = tierConfig[tier];
  const icons = getMembershipCardIcon(tier);

  return (
    <PortraitCardContainer
      backgroundColor={config.backgroundColor}
      borderColor={config.borderColor}
      mixBlendMode={config.mixBlendMode}
    >
      {!!onClose && (
        <IconButton
          size="small"
          aria-label={intl.formatMessage({ id: 'navigation.close' })}
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
          }}
        >
          <Icon name="Close" />
        </IconButton>
      )}
      <GradientImage variant="portrait" />
      {!!config?.subLayer && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: config.subLayer,
            mixBlendMode: 'soft-light',
            opacity: 0.3,
          }}
        />
      )}
      <NoiseImage variant="portrait" />
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: 100,
          maxHeight: '60%',
          marginBottom: logoExtraSpace ? 20 : 0,
        }}
      >
        <Image
          src={config.textImage}
          fill
          alt="Loyalty Logo"
          style={{
            mixBlendMode: config.mixBlendMode,
            objectFit: 'contain',
          }}
          unoptimized
          priority
        />
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      {!isProgressAnimation && (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            px: 3,
            pb: !!children ? 2 : 3,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Image
            src={icons.nf}
            height={20}
            width={48}
            alt="Nofomo Logo"
            style={{
              mixBlendMode: config.mixBlendMode,
              opacity: !!config?.subLayer ? 0.6 : 0.9,
              objectFit: 'contain',
            }}
            unoptimized
            priority
          />
        </Box>
      )}
      {children}
    </PortraitCardContainer>
  );
};

export default LoyaltyCardPortrait;
